/**
 * ReportCompliance.js — NIST CSF 2.0, ISO 27001, PCI-DSS, GDPR mapping
 */
import React, { useMemo } from "react";
import { getMitre, NIST_MAP, SEVERITY_COLORS } from "../reportUtils";

const ISO_MAP = {
  "Reconnaissance":   { control: "A.8.8",  name: "Management of Technical Vulnerabilities" },
  "Initial Access":   { control: "A.8.20", name: "Networks Security" },
  "Credential Access":{ control: "A.8.5",  name: "Secure Authentication" },
  "Command & Control":{ control: "A.8.16", name: "Monitoring Activities" },
  "Lateral Movement": { control: "A.8.22", name: "Segregation of Networks" },
  "Exfiltration":     { control: "A.8.12", name: "Data Leakage Prevention" },
  "Impact":           { control: "A.8.14", name: "Redundancy of Information Processing" },
  "Defense Evasion":  { control: "A.8.16", name: "Monitoring Activities" },
  "Discovery":        { control: "A.8.6",  name: "Capacity Management" },
};

const PCI_MAP = {
  "Reconnaissance":   "Req 11.3 — Penetration Testing",
  "Initial Access":   "Req 6.3 — Vulnerability Management",
  "Credential Access":"Req 8.3 — Authentication",
  "Command & Control":"Req 10.6 — Log Monitoring",
  "Lateral Movement": "Req 1.3 — Network Access Controls",
  "Exfiltration":     "Req 3.4 — Protect Stored Data",
  "Impact":           "Req 12.10 — Incident Response Plan",
  "Discovery":        "Req 11.1 — Network Discovery",
};

const GDPR_RELEVANCE = {
  "Exfiltration":     { article: "Art. 33", name: "Breach Notification (72h)", severity: "critical" },
  "Command & Control":{ article: "Art. 32", name: "Security of Processing",    severity: "high"     },
  "Initial Access":   { article: "Art. 25", name: "Data Protection by Design", severity: "high"     },
  "Credential Access":{ article: "Art. 32", name: "Security of Processing",    severity: "high"     },
  "Lateral Movement": { article: "Art. 32", name: "Access Control Measures",   severity: "medium"   },
  "Impact":           { article: "Art. 33", name: "Breach Notification (72h)", severity: "critical" },
};

export default function ReportCompliance({ stats, theme, SEVERITY_COLORS: SC }) {
  if (!stats) return null;

  const detectedTactics = useMemo(() => {
    const tactics = new Set();
    stats.attackDist.forEach(a => {
      const m = getMitre(a.name);
      tactics.add(m.tactic);
    });
    return [...tactics].filter(t => t !== "Unknown");
  }, [stats]);

  const nistFindings = detectedTactics.map(t => ({ tactic: t, ...NIST_MAP[t] })).filter(f => f.function);
  const isoFindings  = detectedTactics.map(t => ({ tactic: t, ...ISO_MAP[t]  })).filter(f => f.control);
  const pciFindings  = detectedTactics.map(t => ({ tactic: t, req: PCI_MAP[t] })).filter(f => f.req);
  const gdprFindings = detectedTactics.map(t => ({ tactic: t, ...GDPR_RELEVANCE[t] })).filter(f => f.article);

  const NIST_FUNCTIONS = [
    { fn: "GOVERN",   color: theme.colors.accent, desc: "Risk governance and strategy" },
    { fn: "IDENTIFY", color: theme.colors.accent, desc: "Asset and risk identification" },
    { fn: "PROTECT",  color: "#2E7D32", desc: "Safeguards and access control" },
    { fn: "DETECT",   color: "#F57F17", desc: "Anomaly and event detection" },
    { fn: "RESPOND",  color: "#C62828", desc: "Incident response activities" },
    { fn: "RECOVER",  color: "#00695C", desc: "Recovery and restoration" },
  ];

  const nistCoverage = new Set(nistFindings.map(f => f.function));

  return (
    <div className="report-section page-break" style={{ marginBottom: 24 }}>
      <SectionTitle color="#E65100">Compliance Mapping</SectionTitle>

      {/* NIST CSF 2.0 */}
      <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <SubTitle theme={theme}>NIST Cybersecurity Framework 2.0</SubTitle>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8, marginBottom: 16 }}>
          {NIST_FUNCTIONS.map(({ fn, color, desc }) => {
            const active = nistCoverage.has(fn);
            return (
              <div key={fn} style={{ background: active ? `${color}18` : theme.colors.tableHeader, border: `2px solid ${active ? color : theme.colors.cardBorder}`, borderRadius: 8, padding: "12px 8px", textAlign: "center" }}>
                <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color: active ? color : theme.colors.textMuted }}>{fn}</div>
                <div style={{ fontFamily: "monospace", fontSize: 10, color: active ? theme.colors.textSecondary : theme.colors.textMuted, marginTop: 4, lineHeight: 1.3 }}>{desc}</div>
                {active && <div style={{ fontFamily: "monospace", fontSize: 10, color, marginTop: 6, fontWeight: 700 }}>⚠ GAPS</div>}
              </div>
            );
          })}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
          <thead>
            <tr style={{ background: theme.colors.tableHeader }}>
              {["NIST Function","Category","Control Requirement","Detected Tactic"].map(h => (
                <th key={h} style={{ padding: "7px 10px", textAlign: "left", color: theme.colors.textPrimary, borderBottom: `2px solid ${theme.colors.cardBorder}`, fontSize: 10 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nistFindings.map((f, i) => {
              const fnColor = NIST_FUNCTIONS.find(n => n.fn === f.function)?.color || "#1565C0";
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? theme.colors.rowAlt : theme.colors.card, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                  <td style={{ padding: "7px 10px" }}>
                    <span style={{ padding: "2px 6px", background: `${fnColor}18`, border: `1px solid ${fnColor}44`, borderRadius: 4, color: fnColor, fontSize: 9, fontWeight: 700 }}>{f.function}</span>
                  </td>
                  <td style={{ padding: "7px 10px", color: theme.colors.accent, fontWeight: 700 }}>{f.category}</td>
                  <td style={{ padding: "7px 10px", color: theme.colors.textSecondary }}>{f.control}</td>
                  <td style={{ padding: "7px 10px", color: "#C62828" }}>{f.tactic}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* ISO 27001 */}
        <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20 }}>
          <SubTitle theme={theme}>ISO/IEC 27001:2022 Controls</SubTitle>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 10 }}>
            <thead>
              <tr style={{ background: theme.colors.tableHeader }}>
                {["Control","Name","Tactic"].map(h => <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: theme.colors.textPrimary, borderBottom: `2px solid ${theme.colors.cardBorder}` }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {isoFindings.map((f, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? theme.colors.rowAlt : theme.colors.card, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                  <td style={{ padding: "6px 8px", color: theme.colors.accent, fontWeight: 700 }}>{f.control}</td>
                  <td style={{ padding: "6px 8px", color: theme.colors.textPrimary }}>{f.name}</td>
                  <td style={{ padding: "6px 8px", color: "#C62828" }}>{f.tactic}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PCI-DSS + GDPR */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* PCI-DSS */}
          <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 16 }}>
            <SubTitle theme={theme}>PCI-DSS v4.0</SubTitle>
            {pciFindings.length === 0 ? (
              <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted }}>No direct PCI-DSS findings detected</div>
            ) : pciFindings.map((f, i) => (
              <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid #F0F0F0", fontFamily: "monospace", fontSize: 10 }}>
                <span style={{ color: theme.colors.accent, fontWeight: 700 }}>{f.req}</span>
                <span style={{ color: theme.colors.textSecondary, marginLeft: 8 }}>{f.tactic}</span>
              </div>
            ))}
          </div>

          {/* GDPR */}
          <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 16 }}>
            <SubTitle theme={theme}>GDPR Relevance</SubTitle>
            {gdprFindings.length === 0 ? (
              <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted }}>No direct GDPR findings detected</div>
            ) : gdprFindings.map((f, i) => {
              const color = SC[f.severity] || SC.info;
              return (
                <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.08)", fontFamily: "monospace", fontSize: 11, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ color: "#C62828", fontWeight: 700 }}>{f.article}</span>
                    <span style={{ color: theme.colors.textSecondary, marginLeft: 8, fontWeight: 600 }}>{f.name}</span>
                  </div>
                  <span style={{ padding: "2px 7px", background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 3, color, fontSize: 10, fontWeight: 700 }}>{f.severity.toUpperCase()}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

const SectionTitle = ({ children, color }) => (
  <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color, letterSpacing: 2, textTransform: "uppercase", borderLeft: `4px solid ${color}`, paddingLeft: 12, marginBottom: 16 }}>{children}</div>
);
const SubTitle = ({ children, theme }) => (
  <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 10, fontWeight: 700 }}>{children}</div>
);
